import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Input, Button, Radio, RadioGroup, FormControlLabel, FormControl, Select, MenuItem, TextField } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";

interface State {
  editMode: string,
  stockCheckMngNo: string
}

const defState = {
  editMode: "add",
  stockCheckMngNo: ""
};

interface Info {
  stockCheckMngNo: string,
  mngName: string,
  useYn: string,
  regName: string,
  regDate: string,
  mngDesc: string
}

const defInfo = {
  stockCheckMngNo: "",
  mngName: "",
  useYn: "Y",
  regName: "",
  regDate: "",
  mngDesc: ""
};

const validationMsg = [
  {id: "mngName", type: "NotBlank", msg: "실사명을 입력해 주세요."},
  {id: "mngName", type: "Size", msg: "실사명을 확인해 주세요."},
  {id: "mngName", type: "Duplicate", msg: "이미 등록된 실사명입니다."},
  {id: "useYn", type: "NotBlank", msg: "사용여부를 선택해 주세요."},
  {id: "useYn", type: "Size", msg: "사용여부를 확인해 주세요."}
];

function InventoryAuditManageDetail(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);
  
  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const query = Object.fromEntries(new URLSearchParams(location.search).entries());
    state.editMode = query.stockCheckMngNo ? "update" : "add";
    state.stockCheckMngNo = query.stockCheckMngNo || "";
    setState({ ...state });
  }

  async function searchInfo() {
    if (!state.stockCheckMngNo) {
      return;
    }

    try {
      const data = await kutil.sendGet("/api/stock/inventory_audit_manage_detail.data?mode=search", {stockCheckMngNo: state.stockCheckMngNo}, {});
      setInfo(data.info);
    } catch(e) {
      console.log(e);
    }
  }

  async function save() {
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    const confirmMsg = state.editMode == "update" ? "수정" : "등록";
    if (await kutil.confirm(confirmMsg + "하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/inventory_audit_manage_detail.proc?mode=" + state.editMode, info, {
        validationMsg: validationMsg
      });
      if (state.editMode == "update") {
        await kutil.alert("수정하였습니다.");
        router.reload();
      } else if (state.editMode == "add") {
        await kutil.alert("등록하였습니다.");
        goList();
      }
    } catch(e) {
      console.log(e);
    }
  }

  function goList() {
    router.navigateByPath("/stock/inventory_audit_manage_list", JSON.parse(decodeURI(router.query("searchParams") || "{}")));
  }

  return (
  <>
    {/* contents */}
    <div className="contents detail">
      {/* contents 상단 */}
      <div className="contents-top">
        {/* 타이틀 영역 */}
        <div className="title-area">
          {/* 타이틀 */}
          <p>재고실사 관리</p>
        </div>
        {/*// 타이틀 영역 */}
        {/* 버튼 영역 */}
        <div className="top-btn-area">
          <Button variant="outlined" onClick={goList}>{ state.editMode == "add" ? "취소" : "목록" }</Button>
          { auth.write &&
          <Button variant="contained" onClick={save}>{ state.editMode == "add" ? "등록" : "수정" }</Button>
          }
        </div>
        {/*// 버튼 영역 */}
      </div>
      {/*// contents 상단 */}
      {/* 내용 */}
      <div className="table-wrap">
        <table>
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "17%" }} />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row">실사명<em>*</em></th>
              <td>
                <Input
                  inputProps={{ maxLength: 1000 }}
                  value={info.mngName}
                  onChange={(e) => {setInfo(prev => ({ ...prev, mngName: e.target.value }))}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  disabled={state.editMode=="update"}
                />
              </td>
              <th scope="row">사용여부</th>
              <td>
                <div className="form-base">
                  <FormControl>
                    <RadioGroup
                      row
                      value={info.useYn}
                      onChange={(e) => {setInfo(prev => ({ ...prev, useYn: e.target.value }))}}
                    >
                    {(commonCode.useYn as any[]).map((code) => (
                      <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                    ))}
                    </RadioGroup>
                  </FormControl>
                </div>
              </td>
              <th scope="row">등록자</th>
              <td>
                { info.regName }
              </td>
              <th scope="row">등록일</th>
              <td>
                { kutil.date2string(info.regDate, "yyyy-MM-dd HH:mm:ss") }
              </td>
            </tr>
            <tr>
              <th scope="row">설명</th>
              <td colSpan={7}>
                <TextField
                  value={info.mngDesc}
                  onChange={(e) => {setInfo(prev => ({ ...prev, mngDesc: e.target.value }))}}
                  multiline
                  rows={4}
                  fullWidth 
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/*// 내용 */}
    </div>
    {/*// contents */}
  </>
  );
}

export default React.memo(InventoryAuditManageDetail);
